import React from "react";
import _ from "lodash";
import * as Team from "src/state/modules/team/types";
import Styles from "./TeamsList.module.scss";
import { ColumnSizes } from "src/components/atoms/OldGrid/OldColumn";
import { Text } from "src/components/atoms/Text/Text";
import { TeamRow } from "./TeamRow";
import { useFavoriteTeams } from "src/components/util/useFavoriteTeams";

export const columnSizes: {
  [rowName: string]: ColumnSizes;
} = {
  teamsList: { md: 16, lg: 16, xl: 16 },
  teamsListOffset: { md: 4, lg: 4, xl: 4 },
};

interface SeasonHeaderProps {
  displaySeason: string;
}

const SeasonHeader = ({ displaySeason }: SeasonHeaderProps) => {
  return (
    <div className={Styles.seasonHeader}>
      <Text weight={"xbold"}>{displaySeason}</Text>
    </div>
  );
};

interface SeasonAndTeamsProps {
  teamList: Team.ListTeam[];
}

const SeasonAndTeams = ({ teamList }: SeasonAndTeamsProps) => {
  const indexOfLastStaffTeam = _.findLastIndex(teamList, Team.isAdminOfListItem);

  return (
    <>
      <SeasonHeader displaySeason={teamList[0].displaySeason} />
      <div className={Styles.seasonContainer}>
        {teamList.map((team, i) => {
          if (team !== undefined)
            return <TeamRow key={team.id} team={team} isLastStaffTeam={i !== 0 && indexOfLastStaffTeam === i} />;
          return <></>;
        })}
      </div>
    </>
  );
};

interface Props {
  teams?: Team.TeamListGroupedBySeason[];
  Interstitial: React.ComponentType<any>;
}

interface PopulatedProps {
  teams: Team.TeamListGroupedBySeason[];
}

// The favorites section reuses the season layout (header + bordered container of TeamRows).
const PinnedSection = ({ teams }: { teams: Team.ListTeam[] }) => (
  <>
    <div className={Styles.seasonHeader}>
      <Text weight={"xbold"}>★ Pinned</Text>
    </div>
    <div className={Styles.seasonContainer}>
      {teams.map((team) => (
        <TeamRow key={`pinned-${team.id}`} team={team} isLastStaffTeam={false} />
      ))}
    </div>
  </>
);

const PopulatedTeamsPage = ({ teams }: PopulatedProps) => {
  const { favorites } = useFavoriteTeams();

  // Pinned teams float to a section at the very top, in the order they appear in the list. They're
  // then removed from their own season group below, so each team shows exactly once.
  const pinned: Team.ListTeam[] = [];
  for (const season of teams) {
    for (const teamList of Object.values(season)) {
      for (const team of teamList) {
        if (favorites.has(team.id)) pinned.push(team);
      }
    }
  }

  return (
    <>
      {pinned.length > 0 && <PinnedSection teams={pinned} />}
      {teams.map((season, si) =>
        Object.values(season).map((teamList, i) => {
          const remaining = teamList.filter((team) => !favorites.has(team.id));
          if (remaining.length === 0) return null; // whole season is pinned — don't render an empty group
          return <SeasonAndTeams key={`${si}-${i}`} teamList={remaining} />;
        })
      )}
    </>
  );
};

const TeamsList = ({ teams, Interstitial }: Props) => {
  const contentBlock = (teams: Team.TeamListGroupedBySeason[] | undefined) => {
    if (teams !== undefined) return <PopulatedTeamsPage teams={teams} />;
    return <></>;
  };

  return <div className={Styles.teamListContainer}>{contentBlock(teams)}</div>;
};

export default TeamsList;
